/**
 * The bolt's sheet — what you could start today, and one tap to start it.
 *
 * It always opens, even on a scheduled day: a scheduled session gets a
 * confirming launch card rather than a blind start, with an escape hatch to the
 * whole library. Selection is explicit and the CTA names the pick, so a mistaken
 * tap costs nothing.
 *
 * Two steps only — the frame, and the picker behind its hatch. It never asks
 * about an unfinished session: the session page owns that gate, and a sheet that
 * duplicated it would ask the same question twice.
 *
 * Mobile only by construction. The bolt lives in the bottom tab bar, which the
 * shell renders in place of the sidebar, so there is no viewport where this
 * opens and a centred modal would be the right container.
 */

import {
  ArrowPathIcon,
  BoltIcon,
  CheckIcon,
  MoonIcon,
  PlusIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import {
  START_WORKOUT_COPY,
  avoidLine,
  catchupReason,
  defaultSelection,
  deriveFrame,
  findWorkout,
  footerKind,
  hatchLabel,
  isPickerShowing,
  latePillLabel,
  pickerNote,
  pickerTags,
  planLine,
  planNextView,
  scheduledReason,
  selectionSeedKey,
  sessionLine,
  staleLine,
  startCtaLabel,
  startWorkoutTitle,
  suggestionsView,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import { CalmCard } from './CalmCard';
import { NextUpBlock } from './NextUpBlock';
import { WeekRibbon } from './WeekRibbon';
import { WorkoutPickCard } from './WorkoutPickCard';
import { WorkoutPickerStep } from './WorkoutPickerStep';
import { WorkoutPickRow } from './WorkoutPickRow';

import type {
  StartWorkoutSheetProps,
  PickListProps,
  MainFrameProps,
  AvoidLineProps,
  PlanLineProps,
} from './StartWorkoutSheet.types';
import type { ID } from '@personal-trainer-app/shared/src/types/common/id.types';
import type { StartWorkoutStep } from '@personal-trainer-app/shared/src/utils/workout/start-options';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { CopyRuns } from '@/components/ui/CopyRuns';
import { EmptyState } from '@/components/ui/EmptyState';
import { BottomSheet } from '@/components/ui/Modal';
import { Text } from '@/components/ui/primitives';
import { Skeleton } from '@/components/ui/Progress';
import { useStartWorkoutOptions } from '@/hooks/workouts';

/** One radio group across the card and every row beneath it. */
const PICK_GROUP = 'start-workout-pick';

// =============================================================================
// SMALL PIECES
// =============================================================================

/** The plan line above the ribbon. */
function PlanLine({
  options,
  suffix,
}: PlanLineProps): React.JSX.Element | null {
  const line = planLine(options, suffix);
  if (!line) return null;

  return (
    <Text as="p" variant="body-sm" color="secondary" className="mb-3.5">
      <CopyRuns
        segments={line}
        strongClassName="font-semibold text-neutral-200"
      />
    </Text>
  );
}

/** Which muscles were steered clear of, and why — so a gap is explained. */
function AvoidLine({ options }: AvoidLineProps): React.JSX.Element | null {
  const line = avoidLine(options);
  if (!line) return null;

  // No glyph: a heart is the favourite mark on the exercise page, and the app has
  // never had one for soreness — it says it in words and paints it on the figure.
  return (
    <Text
      as="p"
      variant="body-sm"
      color="muted"
      className="-mt-1.5 mb-3.5 leading-snug"
    >
      {line}
    </Text>
  );
}

// =============================================================================
// THE SHEET
// =============================================================================

export function StartWorkoutSheet({
  isOpen,
  onClose,
  onStart,
  onCreateWorkout,
}: StartWorkoutSheetProps): React.JSX.Element {
  const { options, error, isStale, fetchedAt, pending, showPending, refetch } =
    useStartWorkoutOptions({ enabled: isOpen });

  const frame = useMemo(() => deriveFrame(options, error), [options, error]);

  const [step, setStep] = useState<StartWorkoutStep>('main');
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<{ key: string; id: ID | null }>({
    key: '',
    id: null,
  });

  // Re-seed when the frame's own default changes — a fresh answer landing, or
  // the sheet reopening onto a different day. Set during render, like the muscle
  // map's face reset, so no frame is ever painted with the previous pick.
  const seedKey = selectionSeedKey(frame);
  if (selection.key !== seedKey) {
    setSelection({ key: seedKey, id: defaultSelection(frame) });
  }

  const selectedId = selection.id;
  const select = (id: ID): void => setSelection({ key: seedKey, id });

  useEffect(() => {
    if (!isOpen) {
      setStep('main');
      setQuery('');
    }
  }, [isOpen]);

  const selectedWorkout = options ? findWorkout(options, selectedId) : null;
  const showPicker = isPickerShowing(frame, step);
  const canGoBack = step === 'picker';

  // Back from the picker discards the picker's choice: the frame returns
  // with the pick it had when the hatch was opened, so the card and the CTA
  // never name different workouts. (The scheduled and missed frames draw
  // their own workout, whatever is selected.)
  const [returnTo, setReturnTo] = useState<ID | null>(null);
  const openPicker = (): void => {
    setReturnTo(selectedId);
    setStep('picker');
  };
  const backToFrame = (): void => {
    setSelection({ key: seedKey, id: returnTo });
    setStep('main');
  };

  // -------------------------------------------------------------------------
  // Chrome
  // -------------------------------------------------------------------------

  // `aria-disabled` rather than `disabled`, deliberately. An unpicked CTA should
  // read as an instruction — "Pick a workout to start" — not as a broken button,
  // and `disabled` drops it to 40% opacity. This keeps it at full strength as an
  // outline, still announced as unavailable and still reachable by keyboard.
  const canStart = selectedWorkout !== null;

  const startCta = (
    <Button
      variant={canStart ? 'primary' : 'outline-contrast'}
      fullWidth
      aria-disabled={!canStart}
      leftIcon={<BoltIcon className="h-5 w-5" />}
      onClick={() => selectedWorkout && onStart(selectedWorkout.id)}
      data-testid="start-workout-cta"
    >
      {startCtaLabel(selectedWorkout)}
    </Button>
  );

  const footer = ((): React.ReactNode => {
    switch (footerKind(frame, step)) {
      case 'skeleton':
        return <Skeleton variant="rounded" width="100%" height="3rem" />;
      case 'none':
        return null;
      case 'create':
        return (
          <Button
            variant="primary"
            fullWidth
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={onCreateWorkout}
            data-testid="start-workout-cta"
          >
            {START_WORKOUT_COPY.createWorkout}
          </Button>
        );
      case 'start':
        return startCta;
      case 'choose':
        return (
          <Button
            variant="outline-primary"
            fullWidth
            onClick={openPicker}
            data-testid="start-workout-hatch"
          >
            {START_WORKOUT_COPY.chooseWorkout}
          </Button>
        );
      case 'start_and_hatch':
        // No leading glyph on the escape hatch. `ListBulletIcon` already means
        // "how many exercises" on every workout card, and the app has no icon
        // for "see all" — which is why its other see-all affordances are
        // labelled, not iconic.
        return (
          <div className="flex flex-col items-center gap-2.5">
            {startCta}
            {options && (
              <Button
                variant="ghost"
                fullWidth
                onClick={openPicker}
                data-testid="start-workout-hatch"
              >
                {hatchLabel(options)}
              </Button>
            )}
          </div>
        );
    }
  })();

  // -------------------------------------------------------------------------
  // Body
  // -------------------------------------------------------------------------

  const body = ((): React.ReactNode => {
    if (frame.kind === 'loading') {
      return (
        <div data-testid="start-workout-loading">
          <Skeleton variant="rounded" width="100%" height="3.5rem" />
          <div className="mt-3.5">
            <Skeleton variant="rounded" width="100%" height="9.5rem" />
          </div>
        </div>
      );
    }

    if (frame.kind === 'error') {
      return (
        <Alert
          variant="error"
          title={START_WORKOUT_COPY.errorTitle}
          data-testid="start-workout-error"
        >
          {frame.message}
          <div className="mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              leftIcon={<ArrowPathIcon className="h-4 w-4" />}
              onClick={() => void refetch()}
            >
              {START_WORKOUT_COPY.retry}
            </Button>
          </div>
        </Alert>
      );
    }

    if (!options) return null;

    if (frame.kind === 'empty') {
      return (
        <EmptyState
          variant="bare"
          title={START_WORKOUT_COPY.emptyTitle}
          description={START_WORKOUT_COPY.emptyDescription}
          data-testid="start-workout-empty"
        />
      );
    }

    // A fresher answer landed while this one was on screen and says something
    // different. Offered, never applied: nothing re-sorts under a thumb that is
    // already reaching for a workout. It is an action, so it reads as a
    // neutral button rather than wearing the selected look.
    const late = pending && (
      <Button
        variant="contrast"
        size="sm"
        onClick={showPending}
        className="mb-3"
        data-testid="start-workout-late"
      >
        {latePillLabel(options)}
      </Button>
    );

    const stale = isStale && fetchedAt && (
      <Alert
        variant="neutral"
        icon={<SignalSlashIcon className="h-5 w-5" />}
        className="mb-3"
        data-testid="start-workout-offline"
      >
        <CopyRuns
          segments={staleLine(fetchedAt)}
          strongClassName="font-semibold text-neutral-200"
          figureClassName="font-mono tabular-nums text-neutral-200"
        />
        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowPathIcon className="h-4 w-4" />}
            onClick={() => void refetch()}
          >
            {START_WORKOUT_COPY.retry}
          </Button>
        </div>
      </Alert>
    );

    if (showPicker) {
      return (
        <>
          {stale}
          {late}
          <WorkoutPickerStep
            options={options}
            selectedId={selectedId}
            onSelect={select}
            tags={pickerTags(frame)}
            query={query}
            onQueryChange={setQuery}
            note={pickerNote(frame, options)}
          />
        </>
      );
    }

    return (
      <>
        {stale}
        {late}
        <MainFrame
          frame={frame}
          options={options}
          selectedId={selectedId}
          onSelect={select}
        />
      </>
    );
  })();

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={startWorkoutTitle(frame, step)}
      height="auto"
      onBack={canGoBack ? backToFrame : undefined}
      footer={footer}
      data-testid="start-workout-sheet"
    >
      {body}
    </BottomSheet>
  );
}

// =============================================================================
// THE MAIN FRAMES
// =============================================================================

function MainFrame({
  frame,
  options,
  selectedId,
  onSelect,
}: MainFrameProps): React.JSX.Element | null {
  const ribbon = options.plan?.days && (
    <WeekRibbon days={options.plan.days} today={options.today} />
  );

  switch (frame.kind) {
    case 'scheduled':
      return (
        <>
          <PlanLine options={options} />
          {ribbon}
          <WorkoutPickCard
            workout={frame.pick}
            name={PICK_GROUP}
            onSelect={onSelect}
            reason={scheduledReason()}
          />
        </>
      );

    case 'done':
    case 'extra': {
      const { session } = frame;

      return (
        <>
          <PlanLine options={options} />
          {ribbon}
          <CalmCard
            tone="success"
            icon={<CheckIcon className="h-5 w-5" strokeWidth={2} />}
            title={session.workout.name}
          >
            <Text
              as="p"
              variant="body-sm"
              color="secondary"
              className="mt-1.5 leading-snug"
            >
              <CopyRuns
                segments={sessionLine(session, frame.kind === 'extra')}
                figureClassName="font-mono font-medium tabular-nums text-neutral-200"
              />
            </Text>
            <NextUpBlock nextDue={options.next_due} />
          </CalmCard>
        </>
      );
    }

    case 'rest':
      return (
        <>
          <PlanLine options={options} />
          {ribbon}
          {/*
            Attribute the rest, then answer the question a refused button
            actually raises. The old headline, "Nothing on the plan today",
            spent the card's heaviest line on an absence and was contradicted by
            the line beneath it — rest IS on the plan, which is why that line had
            to correct it. Second person throughout, and the two sentences answer
            "can I train anyway?" before "how does it count?", in that order,
            because that is the order they are asked in.
          */}
          <CalmCard
            icon={<MoonIcon className="h-5 w-5" />}
            title={START_WORKOUT_COPY.restTitle}
          >
            <Text
              as="p"
              variant="body-sm"
              color="secondary"
              className="mt-1.5 leading-snug"
            >
              {START_WORKOUT_COPY.restBody}
            </Text>
            <NextUpBlock nextDue={options.next_due} />
          </CalmCard>
        </>
      );

    case 'catchup':
      return (
        <>
          <PlanLine
            options={options}
            suffix={START_WORKOUT_COPY.catchupSuffix}
          />
          {ribbon}
          <WorkoutPickCard
            workout={frame.missed.workout}
            name={PICK_GROUP}
            onSelect={onSelect}
            reason={catchupReason(frame.missed)}
          />
          <NextUpBlock nextDue={options.next_due} onCard />
        </>
      );

    case 'plan_next':
      return (
        <PickList
          view={planNextView(frame.picks, selectedId)}
          onSelect={onSelect}
        />
      );

    case 'suggestions':
      return (
        <PickList
          view={suggestionsView(frame.picks, selectedId, options.avoid)}
          onSelect={onSelect}
          lead={<AvoidLine options={options} />}
        />
      );

    default:
      return null;
  }
}

/** A pick on its card, the alternatives beneath it — one radio group. */
function PickList({
  view,
  onSelect,
  lead,
}: PickListProps): React.JSX.Element | null {
  if (!view) return null;

  return (
    <>
      <Text as="p" variant="body-sm" color="secondary" className="mb-3.5">
        {view.intro}
      </Text>

      {lead}

      <WorkoutPickCard
        workout={view.workout}
        name={PICK_GROUP}
        onSelect={onSelect}
        reason={view.reason}
      />

      <div className="mt-2 space-y-2">
        {view.rows.map((row) => (
          <WorkoutPickRow
            key={row.workout.id}
            workout={row.workout}
            selected={false}
            onSelect={onSelect}
            name={PICK_GROUP}
            note={row.note}
            tag={row.tag}
            tagTone={row.tagTone}
            caution={row.caution}
          />
        ))}
      </div>
    </>
  );
}

export default StartWorkoutSheet;
